import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPut } from './client';

export interface CatalogueEntry {
  category: 'Cases' | 'Patients' | 'Images' | 'Workflows' | 'AI Review' | 'Webhooks';
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  scope: string;
  description: string;
  sampleBody?: Record<string, unknown>;
}

export interface IntegrationView {
  orgId: string;
  orgName: string;
  productId: string;
  productCode: string;
  productDisplayName: string;
  baseUrl: string;
  authBaseUrl: string;
  clientId: string | null;
  catalogue: CatalogueEntry[];
}

export function useIntegration(orgId: string | undefined, productId: string | undefined) {
  return useQuery({
    queryKey: ['integration', orgId, productId],
    queryFn: () => apiGet<IntegrationView>(`/api/orgs/${orgId}/products/${productId}/integration`),
    enabled: !!orgId && !!productId,
  });
}

/**
 * Build the Postman download URL with `scopes` baked in as a comma
 * list. The browser navigates to this URL to trigger the
 * `Content-Disposition: attachment` response.
 */
export function postmanDownloadUrl(orgId: string, productId: string, scopes: string[]): string {
  const q = scopes.length ? `?scopes=${encodeURIComponent(scopes.join(','))}` : '';
  return `/api/orgs/${orgId}/products/${productId}/integration/postman${q}`;
}

/**
 * Postman environment file paired with the collection. Imported
 * alongside it so `{{baseUrl}}`, `{{clientId}}`, etc. resolve
 * out-of-the-box; `clientSecret` ships empty and the operator pastes
 * it from the admin UI's one-shot reveal modal.
 */
export function postmanEnvironmentDownloadUrl(orgId: string, productId: string): string {
  return `/api/orgs/${orgId}/products/${productId}/integration/postman-environment`;
}

export interface CaseCustomFieldWire {
  field_key: string;
  label: string;
  description: string | null;
  required: boolean;
  max_length: number;
  display_order: number;
}

export interface CaseCustomFieldsResponse {
  etag: string;
  fields: CaseCustomFieldWire[];
}

export async function fetchCaseCustomFields(
  orgId: string,
  productId: string,
): Promise<CaseCustomFieldsResponse> {
  return apiGet<CaseCustomFieldsResponse>(
    `/api/orgs/${orgId}/products/${productId}/case-custom-fields`,
  );
}

export async function saveCaseCustomFields(
  orgId: string,
  productId: string,
  fields: Array<Omit<CaseCustomFieldWire, 'display_order'>>,
): Promise<CaseCustomFieldsResponse> {
  return apiPut<CaseCustomFieldsResponse>(
    `/api/orgs/${orgId}/products/${productId}/case-custom-fields`,
    { fields },
  );
}

/**
 * Build the OpenAPI download URL. The endpoint lives under the
 * IntegrationsController at `orgs/:orgId/products/:productId/integration/openapi.json`.
 */
export function openapiDownloadUrl(orgId: string, productId: string): string {
  return `/api/orgs/${orgId}/products/${productId}/integration/openapi.json`;
}
